// RTK Query slice for the org-level Slack integration (§8) — wire contract
// for the backend router at cleanse-api-v2 `cleanse_api/api/integrations/slack.py`
// (prefix /api/v1/integrations/slack).
//
// Consumers: the integrations-page Slack card (Connect/Disconnect + team name)
// and the custom-builder Notify node's channel picker. The OAuth flow is
// browser-driven: `connectSlack` returns an `authorizeUrl` the card navigates
// to; Slack redirects back to the BE callback which lands the user on
// /integrations?slack_connected=1 (or ?slack_error=...), so the card should
// refetch status on mount/param change rather than expect a mutation result.
//
// OWNER-GATED: until the Cleanlist Slack app is registered, `connectSlack`
// returns 503 and `getSlackChannels` 404s when unconnected — both are normal
// "Connect Slack" states, not errors to toast.

import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithAuth } from '../base-query';
import { expectedOrganizationHeaders } from '@/store/expected-organization-headers';

export interface SlackStatusResponse {
  connected: boolean;
  configured: boolean;
  canInstall: boolean;
  canDisconnect: boolean;
  executionEnabled: boolean;
  denialReason: string | null;
  teamName?: string | null;
  teamId?: string | null;
  status?: string | null;
  installedByUserId?: string | null;
  installedAt?: string | null;
  grantedScopes?: string[];
  missingScopes?: string[];
  /**
   * True when Slack revoked the stored token (invalid_auth / token_revoked /
   * account_inactive — build-v3 §2h K4). The BE nulls the token and reports
   * `connected: false` with the team name kept, so the UI can show a
   * "Reconnect Slack" prompt instead of a fresh-connect state.
   */
  reconnectRequired?: boolean;
}

export interface SlackConnectResponse {
  authorizeUrl: string;
}

export interface SlackChannelDto {
  isExtShared?: boolean;
  isPrivate?: boolean;
  id: string;
  name: string;
  /** When the channel was made (Unix seconds), for newest-first lists. */
  created?: number;
}

export interface SlackChannelsResponse {
  channels: SlackChannelDto[];
  // Private channels list only for a member in them; true when some were
  // left out because the member's Slack account isn't linked to Clu.
  privateNeedsLink?: boolean;
}

export interface SlackDisconnectResponse {
  disconnected: boolean;
}

export interface SlackLinkStatus {
  linked: boolean;
  slackUserId?: string | null;
  teamName?: string | null;
}

/** Personal link reads may isolate cache entries per signed-in member. */
type SlackLinkScope = string | { organizationId: string; userId: string };
const linkOrganization = (scope: SlackLinkScope) =>
  typeof scope === 'string' ? scope : scope.organizationId;

export interface SlackLinkPreview {
  slackUserId: string;
  teamId: string;
  teamName: string;
  cleanlistUserId: string;
  cleanlistEmail: string;
}

export const slackApi = createApi({
  reducerPath: 'slackApi',
  baseQuery: baseQueryWithAuth,
  tagTypes: ['SlackIntegration', 'SlackLink'],
  endpoints: (builder) => ({
    getSlackAgentStatus: builder.query<SlackStatusResponse, string>({
      query: (organizationId) => ({
        url: '/integrations/slack/agent/status',
        headers: expectedOrganizationHeaders(organizationId),
      }),
      providesTags: (_result, _error, organizationId) => [
        { type: 'SlackIntegration', id: `STATUS:${organizationId}` },
      ],
    }),
    connectSlackAgent: builder.mutation<SlackConnectResponse, string>({
      query: (organizationId) => ({
        url: '/integrations/slack/agent/connect',
        method: 'POST',
        headers: expectedOrganizationHeaders(organizationId),
      }),
    }),
    getSlackStatus: builder.query<SlackStatusResponse, string>({
      query: (organizationId) => ({
        url: '/integrations/slack/status',
        method: 'GET',
        headers: expectedOrganizationHeaders(organizationId),
      }),
      providesTags: (_result, _error, organizationId) => [
        { type: 'SlackIntegration', id: `STATUS:${organizationId}` },
      ],
    }),
    connectSlack: builder.mutation<SlackConnectResponse, string>({
      query: (organizationId) => ({
        url: '/integrations/slack/connect',
        method: 'POST',
        headers: expectedOrganizationHeaders(organizationId),
      }),
    }),
    disconnectSlack: builder.mutation<SlackDisconnectResponse, string>({
      query: (organizationId) => ({
        url: '/integrations/slack',
        method: 'DELETE',
        headers: expectedOrganizationHeaders(organizationId),
      }),
      invalidatesTags: (_result, _error, organizationId) => [
        { type: 'SlackIntegration', id: `STATUS:${organizationId}` },
        { type: 'SlackIntegration', id: `CHANNELS:${organizationId}` },
        { type: 'SlackLink', id: organizationId },
      ],
    }),
    getSlackChannels: builder.query<SlackChannelsResponse, string>({
      query: (organizationId) => ({
        url: '/integrations/slack/channels',
        method: 'GET',
        headers: expectedOrganizationHeaders(organizationId),
      }),
      providesTags: (_result, _error, organizationId) => [
        { type: 'SlackIntegration', id: `CHANNELS:${organizationId}` },
      ],
    }),
    getSlackLinkStatus: builder.query<SlackLinkStatus, SlackLinkScope>({
      query: (scope) => ({
        url: '/integrations/slack/link/status',
        headers: expectedOrganizationHeaders(linkOrganization(scope)),
      }),
      providesTags: (_result, _error, scope) => [
        { type: 'SlackLink', id: linkOrganization(scope) },
      ],
    }),
    // Browser half of account linking: consumes the one-time token from the
    // #slack_link= URL the bot DM'd. The signed Slack confirmation is the
    // only thing that can activate the binding. This just sends it.
    previewSlackLink: builder.mutation<
      SlackLinkPreview,
      { browserToken: string; organizationId: string }
    >({
      query: ({ browserToken, organizationId }) => ({
        url: '/integrations/slack/link/preview',
        method: 'POST',
        headers: expectedOrganizationHeaders(organizationId),
        body: { browserToken },
      }),
    }),
    completeSlackLink: builder.mutation<
      { confirmationSent: boolean },
      {
        browserToken: string;
        organizationId: string;
        slackUserId: string;
        cleanlistUserId: string;
      }
    >({
      query: ({
        browserToken,
        organizationId,
        slackUserId,
        cleanlistUserId,
      }) => ({
        url: '/integrations/slack/link/complete',
        method: 'POST',
        headers: expectedOrganizationHeaders(organizationId),
        body: { browserToken, slackUserId, cleanlistUserId },
      }),
      invalidatesTags: (_result, _error, { organizationId }) => [
        { type: 'SlackLink', id: organizationId },
      ],
    }),
    revokeSlackLink: builder.mutation<SlackLinkStatus, string>({
      query: (organizationId) => ({
        url: '/integrations/slack/link',
        method: 'DELETE',
        headers: expectedOrganizationHeaders(organizationId),
      }),
      invalidatesTags: (_result, _error, organizationId) => [
        { type: 'SlackLink', id: organizationId },
      ],
    }),
  }),
});

export const {
  useGetSlackAgentStatusQuery,
  useConnectSlackAgentMutation,
  useGetSlackStatusQuery,
  useDisconnectSlackMutation,
  useGetSlackChannelsQuery,
  useGetSlackLinkStatusQuery,
  usePreviewSlackLinkMutation,
  useCompleteSlackLinkMutation,
} = slackApi;
